import { useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChevronDown } from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { isSunday, parseISO } from "date-fns"
import { useDailyCalendarFilter } from "@/modules/study/stores/use-daily-calendar-filter-store"
import { useDailyCalendarEvents } from "@/modules/study/stores/use-daily-calendar-events"
import type { DailyActivity } from "../../entities/daily-calendar"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"

const getInitial = (name: string) => {
  const words = name.split(" ")
  return words
    .map((w) => w.charAt(0).toUpperCase())
    .join("")
    .slice(0, 1)
}

export default function TopbarFilter() {
  const { getCourseColor } = useCoursesInfo()
  const { activeSubjects, setActiveSubjects } = useDailyCalendarFilter()
  const { allEvents, setFilteredEvents } = useDailyCalendarEvents()

  const activities = useMemo(
    () =>
      allEvents.filter(
        (event): event is DailyActivity => event.dailyCalendarEventType === "activity",
      ),
    [allEvents],
  )

  const subjects = useMemo(
    () =>
      activities
        .filter((activity) => !isSunday(parseISO(activity.dateTime)))
        .map((activity) => activity.subject),
    [activities],
  )

  const uniqueSubjects = useMemo(
    () => Array.from(new Map(subjects.map((subj) => [subj.id, subj])).values()),
    [subjects],
  )

  useEffect(() => {
    setActiveSubjects(uniqueSubjects)
  }, [uniqueSubjects])

  useEffect(() => {
    const dailyEvents = allEvents.filter((event) => event.dailyCalendarEventType === "event")

    const newDailyActivities = allEvents.filter((event) => {
      return (
        event.dailyCalendarEventType === "activity" &&
        activeSubjects.some((subject) => subject.id === event.subject.id)
      )
    })

    setFilteredEvents([...dailyEvents, ...newDailyActivities])
  }, [activeSubjects])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="border-0 text-base" variant="outline">
          Materias <ChevronDown className="ml-4 !size-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        {uniqueSubjects.map((subject) => (
          <DropdownMenuCheckboxItem
            key={subject.id}
            checked={activeSubjects.some((filtered) => filtered.id === subject.id)}
            onCheckedChange={(isChecked) => {
              const news = isChecked
                ? [...activeSubjects, subject]
                : activeSubjects.filter((prev) => prev.id !== subject.id)

              setActiveSubjects(news)
            }}
            className="flex items-center gap-2"
          >
            <Avatar className="h-6 w-6">
              <AvatarFallback
                style={{
                  background: getCourseColor(subject.id),
                }}
                className="text-xs font-medium text-white"
              >
                {getInitial(subject.name)}
              </AvatarFallback>
            </Avatar>
            {subject.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
